import { Outlet, Link, useLocation } from 'react-router-dom'
import { Home, Settings, Plus, Lock } from 'lucide-react'
import { useAppStore } from '../store'
import { Toaster } from './ui/Toaster'

const NAV = [
  { to: '/', label: 'Vaults', icon: Home },
  { to: '/add', label: 'Add vault', icon: Plus },
  { to: '/settings', label: 'Settings', icon: Settings },
]

export function Layout() {
  const location = useLocation()
  const userEmail = useAppStore((s) => s.userEmail)
  const unlockedVault = useAppStore((s) => s.unlockedVault)

  const activeIndex = NAV.findIndex((n) =>
    n.to === '/'
      ? location.pathname === '/'
      : location.pathname.startsWith(n.to) ||
        (n.to === '/add' && location.pathname.startsWith('/create')),
  )
  const onVaultPage = location.pathname.startsWith('/v/')

  return (
    <div className="relative min-h-dvh">
      <div className="ambient" aria-hidden="true">
        <i />
        <i />
        <i />
      </div>

      <header className="sticky top-0 z-30 border-b border-white/[0.06] bg-[rgba(7,7,12,0.55)] backdrop-blur-2xl">
        <div className="mx-auto flex h-16 max-w-[1400px] items-center gap-3 px-4 sm:px-6 lg:px-8">
          <Link
            to="/"
            className="flex items-center gap-2.5 rounded-xl"
            aria-label="Vault home"
          >
            <span className="mark h-9 w-9 rounded-xl">
              <Lock className="h-[17px] w-[17px]" strokeWidth={2} />
            </span>
            <span className="font-display text-[19px] font-semibold tracking-tight">
              Vault
            </span>
          </Link>

          {unlockedVault && !onVaultPage && (
            <Link
              to={`/v/${unlockedVault.id}`}
              className="chip ml-2 hidden sm:inline-flex"
              title="Return to unlocked vault"
            >
              <span className="dot animate-slow-pulse bg-[#5EE0A6]" />
              <span className="font-mono">{unlockedVault.id.slice(0, 5)}…</span>
              unlocked
            </Link>
          )}

          <div className="ml-auto flex items-center gap-3">
            {userEmail && (
              <span className="hidden max-w-[220px] truncate text-sm text-faint lg:block">
                {userEmail}
              </span>
            )}
            <nav
              className="seg glass"
              aria-label="Main"
              style={
                {
                  '--i': Math.max(activeIndex, 0),
                  '--n': NAV.length,
                } as React.CSSProperties
              }
            >
              <span
                className="seg-pill"
                aria-hidden="true"
                style={{
                  opacity: activeIndex < 0 ? 0 : 1,
                  transition: 'transform .55s var(--spring), opacity .3s',
                }}
              />
              {NAV.map((item, i) => {
                const Icon = item.icon
                const active = i === activeIndex
                return (
                  <Link
                    key={item.to}
                    to={item.to}
                    aria-current={active ? 'page' : undefined}
                    aria-label={item.label}
                    className={`seg-btn flex min-h-[40px] items-center justify-center gap-2 sm:min-w-[118px] ${
                      active ? '!text-white' : ''
                    }`}
                  >
                    <Icon className="h-[18px] w-[18px]" />
                    <span className="hidden sm:inline">{item.label}</span>
                  </Link>
                )
              })}
            </nav>
          </div>
        </div>
      </header>

      <main className="relative mx-auto max-w-[1400px] px-4 pb-16 pt-6 sm:px-6 lg:px-8 lg:pt-8">
        <Outlet />
      </main>

      <Toaster />
    </div>
  )
}
